import { Component, EventEmitter, Input, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';

/** Shared control scale: 's' is 32px, 'm' is 40px. Keep in step with ptl-sg-input and ptl-button. */
export type SgSelectSize = 's' | 'm';

export interface SelectOption<T = unknown> {
  value: T;
  label: string;
  fontFamily?: string;
}

@Component({
  selector: 'ptl-select',
  templateUrl: './sg-select.component.html',
  styleUrls: ['./sg-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SgSelectComponent),
      multi: true,
    },
  ],
})
export class SgSelectComponent<T = unknown> implements ControlValueAccessor {
  @Input() options: SelectOption<T>[] = [];
  @Input() placeholder = '';
  @Input() label = '';
  @Input() disabled = false;
  @Input() size: SgSelectSize = 'm';

  @Output() selectionChange = new EventEmitter<T>();

  value: T | null = null;
  readonly panelClass = 'ptl-select-panel';

  get selectedOptionLabel(): string {
    return this.options.find((option) => option.value === this.value)?.label ?? '';
  }

  get selectedOptionFontFamily(): string | undefined {
    return this.options.find((option) => option.value === this.value)?.fontFamily;
  }

  private onChange: (value: T | null) => void = () => undefined;
  private onTouched = () => undefined;

  writeValue(value: T | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onSelectionChange(event: MatSelectChange): void {
    this.value = event.value;
    this.onChange(this.value);
    this.selectionChange.emit(this.value);
  }

  onBlur(): void {
    this.onTouched();
  }
}
